import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import { PurchaseRequestRejectedBy } from "../../../models/PurchaseRequest/PurchaseRequestBody";
import {
  PurhcaseRequestDetails,
  PurhcaseRequestFilters,
} from "../../../models/PurchaseRequest/PurchaseRequestDetails";
import { PurchaseRequestProduct } from "../../../models/PurchaseRequest/PurchaseRequestProductInsights";
import {
  PurchaseRequestActivityLog,
  PurchaseRequestActivityLogFilters,
} from "../../../models/PurchaseRequest/PurchaseRequestActivityLog";
import { VendorsPOReportAPIFilters } from "../../../models/Vendors/VendorsPOReportFilters";
import {
  deleteAPI,
  downloadPDF,
  getAPI,
  patchAPI,
  postAPI,
} from "../../../api/Networking";
import {
  RemoveProductResult,
  RemoveSkuResult,
  UpdateGstBody,
  UpdateProductGstResult,
  UpdateProductRateResult,
  UpdateRateBody,
  UpdateSkuGstResult,
  UpdateSkuQuantityBody,
  UpdateSkuQuantityResult,
  UpdateSkuRateResult,
} from "../../../models/PurchaseRequest/PurchaseRequestRateGstUpdates";
import { OrderSkuRemainingQty } from "../../../models/PurchaseRequest/PrProductAvailableQty";
import { ShortClosePoLineInput } from "../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";

export default class PurchaseRequestAPI {
  getPurchaseRequestListing = async (
    filters: PurhcaseRequestFilters,
    exportCsv: boolean = false,
  ): Promise<PaginatedAPIResponse<PurhcaseRequestDetails>> => {
    try {
      const queryParams = new URLSearchParams();

      if (
        filters.pr_created_by_admin_ids &&
        Array.isArray(filters.pr_created_by_admin_ids)
      ) {
        const adminIds = filters.pr_created_by_admin_ids
          .map((item) => item.id)
          .join(",");
        queryParams.append("pr_created_by_admin_ids", adminIds);
      }

      if (filters.order_id && Array.isArray(filters.order_id)) {
        const orderIds = filters.order_id.map((item) => item.id).join(",");
        queryParams.append("order_id", orderIds);
      }

      if (filters.page_no !== undefined)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit !== undefined)
        queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text);

      if (filters.sort_by) queryParams.append("sort_by", filters.sort_by);

      if (filters.sort_type) queryParams.append("sort_type", filters.sort_type);

      if (filters.expected_payment_date_from)
        queryParams.append(
          "expected_payment_date_from",
          filters.expected_payment_date_from.format("YYYY-MM-DD"),
        );

      if (filters.expected_payment_date_to)
        queryParams.append(
          "expected_payment_date_to",
          filters.expected_payment_date_to.format("YYYY-MM-DD"),
        );

      if (filters.created_date_from)
        queryParams.append(
          "created_date_from",
          filters.created_date_from.format("YYYY-MM-DD"),
        );

      if (filters.created_date_to)
        queryParams.append(
          "created_date_to",
          filters.created_date_to.format("YYYY-MM-DD"),
        );

      if (filters.pending_approval_type?.length) {
        filters.pending_approval_type.forEach((type) => {
          queryParams.append(type, "true");
        });
      }

      if (filters.approved_type?.length) {
        filters.approved_type.forEach((type) => {
          queryParams.append(type, "true");
        });
      }

      if (filters.pr_status?.length) {
        queryParams.append("pr_status", String(filters.pr_status[0].id));
      }

      if (filters.urgent_payment?.length) {
        filters.urgent_payment.forEach((item) => {
          const id = item.id;
          if (!id || id === "-1") return;
          if (id === "true") {
            queryParams.append("is_urgent_sourcing_request", "true");
            return;
          }
          queryParams.append("urgent_payment_status", id);
        });
      }

      if (exportCsv) {
        queryParams.delete("page_no");
        queryParams.delete("limit");
        // Background job (POD pattern): BE emails the CSV; response is { success, message }.
        const exportResponse = await getAPI(
          `/v5/purchase-requests/export?${queryParams.toString()}`,
        );
        return exportResponse as unknown as PaginatedAPIResponse<PurhcaseRequestDetails>;
      }

      const response = await getAPI(
        `/v5/purchase-requests?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  approvePurchaseRequest = async (
    prId: number,
    team: PurchaseRequestRejectedBy,
  ) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/approve-request/${prId}`,
        {
          team: team,
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  rejectPurchaseRequest = async (
    prId: number,
    team: PurchaseRequestRejectedBy,
    rejectionReason?: string,
  ) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/reject-request/${prId}`,
        {
          team: team,
          rejection_reason: rejectionReason,
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  generatePO = async (prId: number) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/generate-po/${prId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestDetailByPOId = async (
    poId: number,
  ): Promise<PurhcaseRequestDetails> => {
    try {
      const response = await getAPI(
        `/v5/purchase-requests/details/po-id/${poId}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestDetail = async (
    prId: number,
  ): Promise<PurhcaseRequestDetails> => {
    try {
      const response = await getAPI(`/v5/purchase-requests/${prId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updatePurchaseRequestWarehouse = async (
    prId: number,
    payload: {
      warehouse_id: string;
      warehouse_address: {
        address_line_1: string | null;
        address_line_2: string | null;
        landmark: string | null;
        city: string | null;
        state: string | null;
        pincode: string | null;
        country_code: string | null;
        contact_name: string | null;
        contact_phone: string | null;
        gstin: string | null;
      };
    },
  ) => {
    try {
      const response = await patchAPI(
        `/v1/sourcing/purchase-requests/${prId}/warehouse`,
        payload,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPrProductAvailableQty = async (
    productIds: number[],
    orderId: number,
  ): Promise<OrderSkuRemainingQty[]> => {
    try {
      const response = await postAPI(
        `/v1/sourcing/purchase-requests/product-available-qty`,
        { product_ids: productIds, order_id: orderId },
      );
      return response.result ?? [];
    } catch (error) {
      throw error;
    }
  };

  downloadPurchaseOrder = async (po_id: number) => {
    try {
      const response = await downloadPDF(
        `/v5/purchase-requests/download-po/${po_id}`,
      );
      return response as Blob;
    } catch (error) {
      throw error;
    }
  };

  updatePurchaseRequestProduct = async (product: PurchaseRequestProduct) => {
    const response = await postAPI(`/v5/purchase-requests/update-pr-product`, {
      product: product,
    });
    return response;
  };

  updatePurchaseRequestProductGst = async (
    prId: number,
    productId: number,
    body: UpdateGstBody,
  ): Promise<UpdateProductGstResult> => {
    try {
      const response = await patchAPI(
        `/v1/sourcing/purchase-requests/${prId}/products/${productId}/gst`,
        body,
      );
      return response.result as UpdateProductGstResult;
    } catch (error) {
      throw error;
    }
  };

  updatePurchaseRequestProductRate = async (
    prId: number,
    productId: number,
    body: UpdateRateBody,
  ): Promise<UpdateProductRateResult> => {
    try {
      const response = await patchAPI(
        `/v1/sourcing/purchase-requests/${prId}/products/${productId}/rate`,
        body,
      );
      return response.result as UpdateProductRateResult;
    } catch (error) {
      throw error;
    }
  };

  updatePurchaseRequestSkuGst = async (
    prId: number,
    sku: string,
    body: UpdateGstBody,
  ): Promise<UpdateSkuGstResult> => {
    try {
      const response = await patchAPI(
        `/v1/sourcing/purchase-requests/${prId}/sku-variants/${encodeURIComponent(sku)}/gst`,
        body,
      );
      return response.result as UpdateSkuGstResult;
    } catch (error) {
      throw error;
    }
  };

  updatePurchaseRequestSkuRate = async (
    prId: number,
    sku: string,
    body: UpdateRateBody,
  ): Promise<UpdateSkuRateResult> => {
    try {
      const response = await patchAPI(
        `/v1/sourcing/purchase-requests/${prId}/sku-variants/${encodeURIComponent(sku)}/rate`,
        body,
      );
      return response.result as UpdateSkuRateResult;
    } catch (error) {
      throw error;
    }
  };

  updatePurchaseRequestSkuQuantity = async (
    prId: number,
    sku: string,
    body: UpdateSkuQuantityBody,
  ): Promise<UpdateSkuQuantityResult> => {
    try {
      const response = await patchAPI(
        `/v1/sourcing/purchase-requests/${prId}/sku-variants/${encodeURIComponent(sku)}/quantity`,
        body,
      );
      return response.result as UpdateSkuQuantityResult;
    } catch (error) {
      throw error;
    }
  };

  removePurchaseRequestProduct = async (
    prId: number,
    productId: number,
  ): Promise<RemoveProductResult> => {
    try {
      const response = await deleteAPI(
        `/v1/sourcing/purchase-requests/${prId}/products/${productId}`,
      );
      return response.result as RemoveProductResult;
    } catch (error) {
      throw error;
    }
  };

  removePurchaseRequestSkuVariant = async (
    prId: number,
    sku: string,
  ): Promise<RemoveSkuResult> => {
    try {
      const response = await deleteAPI(
        `/v1/sourcing/purchase-requests/${prId}/sku-variants/${encodeURIComponent(sku)}`,
      );
      return response.result as RemoveSkuResult;
    } catch (error) {
      throw error;
    }
  };

  sendEmailToVendor = async (pr_id: number, po_id: number) => {
    const response = await postAPI(
      `/admin/purchaseOrder/${pr_id}/vendor/send-email`,
      {
        po_id: po_id,
      },
    );
    return response;
  };

  updatePOStatus = async (
    po_id: number,
    status: string,
    cancellationReason?: string,
  ) => {
    const response = await postAPI(
      `/admin/purchaseOrder/update-status/` + po_id,
      {
        status: status,
        poCancellingReason: cancellationReason || "",
      },
    );
    return response;
  };

  cancelPurchaseRequest = async (
    pr_id: number,
    cancellationReason?: string,
  ) => {
    const response = await postAPI(
      `/v5/purchase-requests/cancel-request/${pr_id}`,
      {
        cancellation_reason: cancellationReason,
      },
    );
    return response;
  };

  getVendorsPOReportData = async (filters: VendorsPOReportAPIFilters) => {
    const response = await getAPI(
      `/v5/purchase-requests/vendors/po-report-data`,
      filters,
    );
    return response;
  };

  reactivatePO = async (prId: number, poId: number) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/reactivate-po/${prId}`,
        { po_id: poId },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getShortClosePoLines = async (prId: number, poId: number) => {
    try {
      const response = await getAPI(
        `/v5/purchase-requests/short-close-po/${prId}/lines?po_id=${poId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  shortClosePO = async (
    prId: number,
    body: {
      po_id: number;
      lines: ShortClosePoLineInput[];
      reason: string;
    },
  ) => {
    try {
      const response = await postAPI(
        `/v5/purchase-requests/short-close-po/${prId}`,
        body,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  resetSourcingApproval = async (prId: number) => {
    try {
      const response = await postAPI(
        `/v1/sourcing/purchase-requests/${prId}/approvals/sourcing/reset`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  resetFinanceApproval = async (prId: number) => {
    try {
      const response = await postAPI(
        `/v1/sourcing/purchase-requests/${prId}/approvals/finance/reset`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  resetSwageazyApproval = async (prId: number) => {
    try {
      const response = await postAPI(
        `/v1/sourcing/purchase-requests/${prId}/approvals/swageazy/reset`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  resetAllApprovals = async (prId: number) => {
    try {
      const response = await postAPI(
        `/v1/sourcing/purchase-requests/${prId}/approvals/reset-all`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  markUrgentPaymentDone = async (prId: number) => {
    try {
      const response = await postAPI(
        `/v1/sourcing/purchase-requests/${prId}/urgent-payment/mark-done`,
        {},
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestActivityLogs = async (
    prId: number,
    filters: PurchaseRequestActivityLogFilters,
  ): Promise<PaginatedAPIResponse<PurchaseRequestActivityLog>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page_no", String(filters.page_no ?? 1));
      queryParams.append("limit", String(filters.limit ?? 20));

      const response = await getAPI(
        `/v1/sourcing/purchase-requests/${prId}/activity-logs?${queryParams.toString()}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };
}
